/**
 * Statut « effectif » d'une réservation (cahier beta staff).
 *
 * Le cahier retire de l'UI les trois actions de statut manuelles (Confirmer,
 * Terminer, No-show) : une résa est confirmée dès sa création, et son passage à
 * « Terminé » est déduit du temps qui passe plutôt que d'un geste du staff en
 * plein service.
 *
 * Ce calcul est volontairement DÉRIVÉ À L'AFFICHAGE, sans écriture en base :
 *   - aucun job planifié à maintenir, aucune écriture de masse à rejouer ;
 *   - la base garde la vérité saisie (confirmé / annulé / no-show), donc la
 *     règle est réversible en changeant cette seule constante ;
 *   - la future vue « Bilan de service », qui réintroduira le no-show, pourra
 *     réutiliser exactement le même helper.
 */

export type ReservationStatut = 'confirme' | 'annule' | 'termine' | 'no-show';

/**
 * Délai après l'heure prévue au bout duquel une résa confirmée bascule en
 * « Terminé ». 4 h couvre un service complet (arrivée + repas + départ) sans
 * marquer terminée une table encore attablée : à 2 h on afficherait « Terminé »
 * pendant le dessert, à 6 h le service du soir déborderait sur le lendemain.
 */
export const AUTO_TERMINE_AFTER_H = 4;

/** Instant prévu de la résa, ou `null` si date/heure inexploitables. */
function scheduledAt(date: string, heure: string): Date | null {
  const m = (heure ?? '').replace('h', ':').match(/^(\d{1,2}):(\d{2})$/);
  if (!m || !/^\d{4}-\d{2}-\d{2}$/.test(date ?? '')) return null;
  const [y, mo, d] = date.split('-').map(Number);
  const dt = new Date(y, mo - 1, d, parseInt(m[1], 10), parseInt(m[2], 10), 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/**
 * Statut à afficher. Les statuts « décidés » (annulé, no-show, terminé saisi à
 * la main avant ce cahier) sont rendus tels quels ; seule une résa confirmée
 * vieillit automatiquement vers « terminé ».
 */
export function effectiveStatut(
  r: { date: string; heure: string; statut: ReservationStatut | null },
  now: Date = new Date(),
): ReservationStatut {
  const stored = r.statut ?? 'confirme';
  if (stored !== 'confirme') return stored;
  const at = scheduledAt(r.date, r.heure);
  if (!at) return 'confirme';
  return now.getTime() >= at.getTime() + AUTO_TERMINE_AFTER_H * 3600_000
    ? 'termine'
    : 'confirme';
}
